/**
 * SearchInput Atom — controlled debounced search field.
 * @layer atom
 * @package @plerity/ui-core
 */

import { useCallback, useEffect, useRef } from "react";
import { cn } from "../utils/cn";
import type { SearchInputProps } from "./SearchInput.types";

// Inline search icon SVG — no lucide-react dep
function SearchIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4 text-gray-400 dark:text-gray-500 shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

export function SearchInput({
  value,
  onChange,
  onDebouncedChange,
  debounceMs = 300,
  placeholder = "Search…",
  clearable = true,
  leadingIcon,
  "aria-label": ariaLabel = "Search",
  className,
  disabled = false,
}: SearchInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce: schedule onDebouncedChange whenever value changes
  useEffect(() => {
    if (!onDebouncedChange) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onDebouncedChange(value);
    }, debounceMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, debounceMs, onDebouncedChange]);

  const handleClear = useCallback(() => {
    onChange("");
    // Fire debounced immediately on clear so consumers don't wait
    if (onDebouncedChange) {
      if (timerRef.current) clearTimeout(timerRef.current);
      onDebouncedChange("");
    }
    inputRef.current?.focus();
  }, [onChange, onDebouncedChange]);

  const showClear = clearable && value.length > 0 && !disabled;
  const hasLeading = Boolean(leadingIcon) || true; // default to search icon

  return (
    <div className={cn("relative flex items-center", className)}>
      {/* Leading icon slot */}
      <span className="pointer-events-none absolute left-3 flex items-center">{leadingIcon ?? <SearchIcon />}</span>

      {/* biome-ignore lint/a11y/useSemanticElements: type="search" would add WebKit/Chrome's native "x" clear-icon, duplicating the custom clear <button> already rendered below when showClear is true. */}
      <input
        ref={inputRef}
        type="text"
        role="searchbox"
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          "w-full rounded-md border py-2 text-sm transition-colors",
          // border-control: WCAG 1.4.11 >= 3:1 in both themes (gray-300/600 was ~1.5:1).
          "border-control",
          "bg-white dark:bg-gray-800",
          "text-gray-900 dark:text-gray-100",
          "placeholder-gray-400 dark:placeholder-gray-500",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          hasLeading ? "pl-9" : "pl-3",
          showClear ? "pr-9" : "pr-3"
        )}
      />

      {/* Clear button */}
      {showClear && (
        <button
          type="button"
          onClick={handleClear}
          aria-label="Clear search"
          className="absolute right-2 flex items-center justify-center rounded p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <ClearIcon />
        </button>
      )}
    </div>
  );
}

SearchInput.displayName = "SearchInput";
